import React, { useCallback, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';

import Form, { SubmitType } from '../../../../components/form';
import Action from '../../../../components/form/elements/action';
import Button, {
  ButtonStyling,
} from '../../../../components/form/elements/button';
import Checkbox from '../../../../components/form/elements/checkbox';
import Group from '../../../../components/form/elements/group';
import Link from '../../../../components/form/elements/link';
import Show from '../../../../components/form/elements/show';
import Text from '../../../../components/form/elements/text';
import TextArea from '../../../../components/form/elements/text-area';
import Address from '../../../../components/form/elements/address';
import RegistrationNr from '../../../../components/form/elements/registration-nr';
import Watch, { WatchType } from '../../../../components/form/elements/watch';
import Partner from '../../../../interfaces/partner';
import {
  HUMAN_PARTNERS_INDEX,
  HUMAN_PARTNERS_VIEW,
} from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { numberTransform, route, when } from '../../../../services/utils';
import formCss from '../../../../components/form/form.module.css';
import partnerCss from './index.module.css';
import { useWarehouseContext } from '../../../warehouse/movement/deals/warehouse-context';

interface Props {
  closeInstead?: boolean;
  customHeader?: boolean;
  horizontal?: boolean;
}

const schema = yup.object().shape({
  form_of_economic_activity_id: yup.string().required(),
  name: yup.string().max(255).required(),
  registration_number: yup
    .string()
    .matches(/^\d{11}$/, 'Registration number must be 11 digits long!')
    .nullable()
    .transform(numberTransform),
  vat_number: when(
    (_, parent) => parent?.vat_payer,
    yup.string().length(13).required().transform(numberTransform),
    yup.string().nullable().transform(numberTransform)
  ),
  email: yup.string().max(255).email().nullable(),
  bank_account_number: yup
    .string()
    .length(21)
    .nullable()
    .transform(numberTransform),
  legal_address: yup.lazy((addressValue) => {
    if (typeof addressValue === 'object') {
      return yup.object().shape({
        value: yup.string().nullable(),
      });
    } else {
      return yup.string().max(255).nullable();
    }
  }),
  actual_address: yup.lazy((addressValue) => {
    if (typeof addressValue === 'object') {
      return yup.object().shape({
        value: yup.string().nullable(),
      });
    } else {
      return yup.string().max(255).nullable();
    }
  }),
});

export default function CreatePartner({
  closeInstead = false,
  customHeader = null,
  horizontal = false,
}: Props) {
  const { closeModalAddPartner, formMethods } = useWarehouseContext();
  const { t } = useTranslation();
  const history = useHistory();
  const { farmId } = useParams<{ farmId: string }>();
  const [defaultValues] = useState({
    legal_address: '',
    actual_address: '',
    active: true,
  });
  const { partnerService } = useService();
  const [body, setBody] = useState([]);
  const [footer, setFooter] = useState([]);

  const updateVatNumber = ({ value, setValue, getValues }: WatchType) => {
    const vatPayer = getValues('vat_payer');
    if (!vatPayer) {
      setValue('vat_number', null);
      return;
    }
    const registrationNumber = getValues('registration_number') || '';
    const vatNumber = getValues('vat_number') || '';
    if (registrationNumber.length > 0 && vatNumber === '') {
      setValue('vat_number', 'LV'.concat(registrationNumber), {
        shouldValidate: true,
      });
    }
  };

  const copyFromLegalAddress = (form: UseFormReturn) => {
    form.setValue('actual_address', form.getValues('legal_address'));
  };

  const setLegalAddress = (setValue: any, data: any) => {
    setValue('legal_address', { value: data.address, label: data.address });
  };

  const submit = useCallback<SubmitType<Partner>>(
    (form: any) => {
      form.legal_address = form.legal_address?.value;
      form.actual_address = form.actual_address?.value;
      return [
        partnerService.store(farmId, form),
        (response) => {
          if (closeInstead) {
            formMethods.setValue('partner_id', {
              label: response?.name,
              value: response?.id,
            });
            closeModalAddPartner();
          } else {
            history.push(
              route(HUMAN_PARTNERS_VIEW, { farmId, id: response.id })
            );
          }
        },
      ];
    },
    [
      partnerService,
      history,
      farmId,
      closeInstead,
      closeModalAddPartner,
      formMethods,
    ]
  );

  useEffect(() => {
    setBody([
      Group([
        RegistrationNr(
          farmId,
          'form_of_economic_activity_id',
          'name',
          'registration_number',
          'legal_address',
          setLegalAddress,
          horizontal
        ),
        Watch(
          Checkbox(
            'vat_payer',
            null,
            horizontal,
            partnerCss['modal-partner-checkbox'],
            false
          ),
          updateVatNumber
        ),
        Show(
          Watch(
            Text('vat_number', null, null, null, horizontal),
            updateVatNumber
          ),
          'vat_payer',
          (vatPayer: boolean) => vatPayer
        ),
        Text('contact_person', null, null, null, horizontal),
        Text('contact_phone', null, null, null, horizontal),
        Text('email', null, null, null, horizontal),
        Text('bank', null, null, null, horizontal),
        Text('bank_account_number', null, null, null, horizontal),
        Checkbox(
          'active',
          null,
          horizontal,
          partnerCss['modal-partner-checkbox'],
          false
        ),
        Address('legal_address', t('legal_address'), horizontal),
        Address('actual_address', t('actual_address'), horizontal),
        Action(
          'pages.human.partners.copy_from_legal_address',
          copyFromLegalAddress,
          horizontal,
          'with-action-modal'
        ),
        TextArea('comment', null, null, horizontal),
      ]),
    ]);

    setFooter([
      Button('create'),
      closeInstead
        ? Button(
            'cancel',
            () => closeModalAddPartner(),
            null,
            null,
            null,
            true,
            ButtonStyling.cancel
          )
        : Link(route(HUMAN_PARTNERS_INDEX, { farmId }), 'cancel'),
    ]);
  }, [
    setBody,
    setFooter,
    farmId,
    t,
    closeInstead,
    closeModalAddPartner,
    horizontal,
  ]);

  const header = () => {
    return (
      <div className={partnerCss['header']}>
        <h2>{t('pages.human.partners.create_partner')}</h2>
      </div>
    );
  };

  return (
    <Form
      title={t('pages.human.partners.create_partner')}
      mode="create"
      body={body}
      footer={footer}
      values={defaultValues}
      submit={submit}
      schema={schema}
      className={formCss['create-partner-modal-form']}
      header={customHeader ? header : null}
    />
  );
}
